// 發布貼文
import { useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import Axios from "@/shared/api/axios";
import StatusModal from "@/components/ui/StatusModal";
import MessageModal from "@/components/ui/MessageModal";
import EditMediaModal from "./EditMediaModal";
import PostMediaEditor from "./PostMediaEditor";
import PostVisibilityModal from "./PostVisibilityModal";
import PostScheduleModal from "./PostScheduleModal";
import PostSettingBadges from "./PostSettingBadges";
import MediaToolbar from "./MediaToolbar";
import { useTranslation } from "react-i18next";

import { processImage, generateBlurredImage } from "@/shared/utils/imageProcessor";
import { uploadVideoToCF } from "@/shared/utils/uploadVideoToCF";
import { reindexCaptionsAfterDelete } from "@/shared/utils/mediaCaptions";
import { extractTags, stripTags } from "@/shared/utils/tags";
import { pastePlainText } from "@/shared/utils/contentEditableInput";
import { toBackendDateTimeString } from "@/shared/utils/formatDate";
import { POST_IMAGE_SIZES } from "@/shared/constants/mediaSizes";
import placeholder_portrait_40x40 from "@/assets/imgs/placeholders/portrait_40x40.png";
import SafeImg from '@/components/shared/SafeImg';

const MyPost = (props) => {
  const idolId = useSelector((state) => state.user.idolId);
  const { t } = useTranslation();
  const avatarUrl = useSelector((state) => state.user.avatarUrl);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [statusCode, setStatusCode] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [mediaCaptions, setMediaCaptions] = useState({});
  const [content, setContent] = useState('');
  const [visibilityType, setVisibilityType] = useState(0);
  const [unlockPrice, setUnlockPrice] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);

  // 舊的 modal refs (保持兼容性)
  const modalPostSuccess = useRef(null);
  const modalSize = useRef(null);
  const modalType = useRef(null);
  const modalThumbnailError = useRef(null);
  const modalImageProcessError = useRef(null);
  const editMediaModalRef = useRef(null);
  const visibilityModalRef = useRef(null);
  const scheduleModalRef = useRef(null);

  const contentEditableRef = useRef(null);

  // index 一律於 updater 內取得：updater 需為純函數（不可內嵌其他 setState），
  // 且並發選檔時以呼叫當下的長度計算會導致 caption 錯位。
  // 注意：仰賴 images 的 useState 宣告在 mediaCaptions 之前（updater 依宣告順序執行，index 才保證已賦值）
  const appendMedia = useCallback((item) => {
    let appendIndex;
    setImages(prev => {
      appendIndex = prev.length;
      return [...prev, item];
    });
    setMediaCaptions(prev => ({ ...prev, [appendIndex]: '' }));
  }, []);

  const removeVideoFromImages = useCallback((localId) => {
    let removedIndex = -1;
    setImages(prev => {
      const idx = prev.findIndex(item => item.localId === localId);
      if (idx < 0) return prev;
      removedIndex = idx;
      return prev.filter(item => item.localId !== localId);
    });
    setMediaCaptions(prev => (removedIndex < 0 ? prev : reindexCaptionsAfterDelete(prev, removedIndex)));
  }, []);

  const startVideoUpload = useCallback(async (file, localId) => {
    try {
      const { uid } = await uploadVideoToCF(
        file,
        () => Axios.post('/post/get-stream-upload-url', { idol_id: idolId }),
      );
      const videoUrl = URL.createObjectURL(file);
      setImages(prev => prev.map(item =>
        item.localId === localId
          ? { type: 1, uid, url: videoUrl, localId }
          : item
      ));
    } catch (error) {
      console.error('CF upload failed:', error);
      removeVideoFromImages(localId);
      modalThumbnailError.current.show();
    }
  }, [idolId, removeVideoFromImages]);

  const handleFileChange = async (event) => {
    // 先 snapshot 成陣列再清 value：避免 async 迴圈中 FileList 被清空導致漏處理；
    // 同時讓使用者下次能再選相同檔案。
    const files = Array.from(event.target.files);
    event.target.value = '';
    if (files.length === 0) return;

    // 個別檔案驗證失敗只跳過該檔，不放棄整批；錯誤 modal 每批最多顯示一次，避免疊開
    let errorShown = false;
    const showMediaError = (modalRef) => {
      if (errorShown) return;
      errorShown = true;
      modalRef.current.show();
    };
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      // 檢查檔案大小
      const maxSize = isVideo ? 200 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        showMediaError(modalSize);
        continue;
      }

      if (!isImage && !isVideo) {
        showMediaError(modalType);
        continue;
      }

      // 圖片格式檢查
      if (isImage && (
        file.type !== "image/jpg" &&
        file.type !== "image/jpeg" &&
        file.type !== "image/png"
      )) {
        showMediaError(modalType);
        continue;
      }

      try {
        if (isImage) {
          const results = await processImage(file, {
            sizes: POST_IMAGE_SIZES,
          });

          appendMedia({
            type: 0,
            file: results.small,
            big_file: results.big,
            url: URL.createObjectURL(results.small),
            big_url: URL.createObjectURL(results.big),
          });
        } else if (isVideo) {
          // 立即加入 loading 佔位，同時啟動 CF 上傳
          const localId = `${Date.now()}_${i}`;
          appendMedia({ type: 1, uploading: true, localId });
          startVideoUpload(file, localId);
        }
      } catch (error) {
        // 圖片讀取／解碼失敗（例如檔案損毀）：跳過該檔並提示，不放棄整批
        console.error("Error processing media:", error);
        showMediaError(modalImageProcessError);
      }
    }
  };

  const handleDeleteImage = (index) => {
    setImages((prevImages) => prevImages.filter((_, i) => i !== index));
    setMediaCaptions(prev => reindexCaptionsAfterDelete(prev, index));
  };

  const handleEditMedia = (index) => {
    const media = images[index];
    editMediaModalRef.current.show({ ...media, index }, mediaCaptions[index] || '');
  };

  const createPost = async () => {
    // 檢查內容是否為空
    if (!canPost) {
      return;
    }

    setIsLoading(true);

    let createdPost = null;

    // 純同步的字串運算，留在 try 外：filterTags 在 try/catch 之後仍要用來判斷是否刷新熱門標籤
    const currentContent = content.trim();
    const filterTags = extractTags(currentContent);
    const contentWithoutTags = stripTags(currentContent).replace(/\s+$/, '');

    try {
      const formData = new FormData();
      formData.append("idol_id", idolId);
      formData.append("content", contentWithoutTags);

      const needsBlurredImages = [1, 2, 4].includes(visibilityType);
      let imageIndex = 0;
      let videoIndex = 0;

      // 組裝 media payload 單獨接錯：generateBlurredImage 是本地 Promise，reject 的是
      // 普通 Error（無 httpErrorCode），交給外層 catch 只會靜默記 log。這裡明確告知
      // 使用者並中止；return 之後外層 finally 仍會解鎖按鈕。
      try {
        for (const [index, image] of images.entries()) {
          if (image.type === 0) {
            formData.append(`post_files[${imageIndex}]`, image.file);
            formData.append(`big_post_files[${imageIndex}]`, image.big_file);

            if (needsBlurredImages) {
              const blur = await generateBlurredImage(image.file);
              formData.append(`blur_post_files[${imageIndex}]`, blur);
            }

            formData.append(`image_file_captions[${imageIndex}]`, mediaCaptions[index] || '');
            formData.append(`media_order[${index}][type]`, 'image');
            formData.append(`media_order[${index}][index]`, imageIndex);
            imageIndex++;
          } else if (image.type === 1) {
            if (!image.uid) continue; // 防禦：canPost 已擋住，但以防萬一
            formData.append(`video_uids[${videoIndex}]`, image.uid);
            formData.append(`video_file_captions[${videoIndex}]`, mediaCaptions[index] || '');
            formData.append(`media_order[${index}][type]`, 'video');
            formData.append(`media_order[${index}][index]`, videoIndex);
            videoIndex++;
          }
        }
      } catch (error) {
        console.error('Error building post media payload:', error);
        modalImageProcessError.current.show();
        return;
      }

      // tags 陣列處理
      filterTags.forEach((tag, index) => {
        formData.append(`tags[${index}]`, tag);
      });

      // 新增權限設定參數
      formData.append('visibility_type', visibilityType);
      if (unlockPrice !== null && (visibilityType === 2 || visibilityType === 4)) {
        formData.append('unlock_price', unlockPrice);
      }

      // 排程發布：帶 'now' 由後端以自己的當下時間立即發布，帶未來時間＝預約發文；
      // 不送前端算好的當下時間字串，避免網路延遲＋時鐘偏差讓它抵達後端時已成過去
      formData.append('post_created_at', scheduledAt ? toBackendDateTimeString(scheduledAt) : 'now');

      const res = await Axios.post("/post/create", formData);

      if (res.data.status_code === 'SYSTEM_1000') {
        modalPostSuccess.current.show();
        // 初始化
        contentEditableRef.current.innerText = "";
        setContent('');
        setImages([]);
        setMediaCaptions({});
        setVisibilityType(0);
        setUnlockPrice(null);
        setScheduledAt(null);
        createdPost = res.data.data;
      } else {
        setStatusCode(res.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error("Error creating post:", error);
      }
    } finally {
      setIsLoading(false);
    }

    // 呼叫端的更新放在 try 外：貼文已建立成功，回呼若拋錯不該被 catch 誤判成建立失敗
    if (createdPost) {
      // 後端回傳建立好的整筆貼文，交由列表就地插到最前面（不整包重抓，避免牆面貼文跳動）
      props.onPostCreated(createdPost);
      // 帶 tags 的貼文會讓 tag 的 usage_count 變動（也可能是全新 tag），比照編輯/刪除刷新熱門標籤
      if (filterTags.length > 0) props.onTagsChanged();
    }
  };

  const handleInput = () => {
    if (contentEditableRef.current) {
      const newContent = contentEditableRef.current.innerText.trim();
      setContent(newContent);
    }
  };

  // 檢查是否能發布
  const needsMedia = visibilityType === 1 || visibilityType === 2 || visibilityType === 4;
  const hasUploadingVideos = images.some(img => img.uploading);
  const canPost = content.trim().length > 0 && (!needsMedia || images.length > 0) && !hasUploadingVideos;

  return (
    <>
      <div className="p-3 border mb-3 rounded bg-white shadowA">
        <div className="d-flex gap-2">
          <div className="img-sm flex-shrink-0">
            <SafeImg
              className="img-fluid rounded-pill"
              src={avatarUrl || placeholder_portrait_40x40}
              alt="User Avatar"
            />
          </div>
          <div className="w-100">
            <PostSettingBadges
              visibilityType={visibilityType}
              unlockPrice={unlockPrice}
              scheduledAt={scheduledAt}
              onEditVisibility={() => visibilityModalRef.current?.show({ visibilityType, unlockPrice })}
              onEditSchedule={() => scheduleModalRef.current?.show({ scheduledAt })}
              className="mb-2"
            />
            <div className="p-2 border-0 rounded bg-light-gray post-edit-wrap ">
              <div>
                {/* 文本 */}
                <div
                  ref={contentEditableRef}
                  contentEditable="true"
                  className="post-edit"
                  suppressContentEditableWarning={true}
                  placeholder={t('write_post')}
                  onPaste={pastePlainText}
                  onInput={handleInput}
                ></div>

                {/* 使用 PostMediaEditor 組件 */}
                <PostMediaEditor
                  images={images}
                  onDeleteImage={handleDeleteImage}
                  onEditMedia={handleEditMedia}
                  mode="create"
                />
              </div>
            </div>
            {/* 使用 MediaToolbar 組件 */}
            <MediaToolbar
              onFileChange={handleFileChange}
              onVisibilityClick={() => visibilityModalRef.current?.show({ visibilityType, unlockPrice })}
              onScheduleClick={() => scheduleModalRef.current?.show({ scheduledAt })}
              onSubmit={createPost}
              canSubmit={canPost}
              isLoading={isLoading}
              mode="create"
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* 成功訊息 - 維持舊的 MessageModal */}
      <MessageModal ref={modalPostSuccess} status="success">
        <p>{t('post_created_successfully')}</p>
      </MessageModal>

      {/* 檔案大小錯誤 - 維持舊的 MessageModal */}
      <MessageModal ref={modalSize} status="error">
        <p>{t('file_size_error')}</p>
      </MessageModal>

      {/* 檔案類型錯誤 - 維持舊的 MessageModal */}
      <MessageModal ref={modalType} status="error">
        <p>{t('file_type_error')}</p>
      </MessageModal>

      {/* 影片上傳失敗錯誤 */}
      <MessageModal ref={modalThumbnailError} status="error">
        <p>{t('video_upload_failed')}</p>
      </MessageModal>

      {/* 圖片處理失敗（選圖時縮圖、送出時模糊圖產製）*/}
      <MessageModal ref={modalImageProcessError} status="error">
        <p>{t('image_process_failed')}</p>
      </MessageModal>

      {/* API 非成功狀態碼統一由 StatusModal 顯示 */}
      <StatusModal ref={statusModalRef} statusCode={statusCode} />

      <EditMediaModal
        ref={editMediaModalRef}
        onSave={(index, caption) => {
          setMediaCaptions(prev => ({ ...prev, [index]: caption }));
        }}
      />

      <PostVisibilityModal
        ref={visibilityModalRef}
        onConfirm={(data) => {
          setVisibilityType(data.visibilityType);
          setUnlockPrice(data.unlockPrice);
        }}
      />

      <PostScheduleModal
        ref={scheduleModalRef}
        onConfirm={(data) => {
          setScheduledAt(data.scheduledAt);
        }}
      />

    </>
  );
};

export default MyPost;